import apiClient from './meta-client';
import type {
    BroadcastResponse,
    PaginatedBroadcastGroups,
    BroadcastGroupDetail,
    PaginatedExecutions,
    CreateBroadcastRequest,
    UpdateBroadcastRequest,
    ValidateVariablesRequest,
    ValidateVariablesResponse,
    VariablesCopyRequest,
    VariablesCopyResponse,
    CancelBroadcastGroupResponse,
    BroadcastFilterOptions,
} from '@/types/broadcast';

// ========== BROADCASTS API ==========

// Listar todos os broadcasts do usuário autenticado (paginado por grupo)
export const getBroadcasts = async (
    page: number = 1,
    perPage: number = 20
): Promise<PaginatedBroadcastGroups> => {
    const response = await apiClient.get<PaginatedBroadcastGroups>('/meta/broadcasts', {
        params: { page, per_page: perPage },
    });
    return response.data;
};

// Listar broadcasts a nível de grupo (paginado).
// Aceita filtros opcionais aplicados server-side via /broadcasts/groups.
export const getBroadcastGroups = async (
    page: number = 1,
    perPage: number = 20,
    dateFrom?: string,
    filters?: {
        categoryId?: number | null;
        metaUserName?: string | null;
        projectId?: number | null;
    },
): Promise<PaginatedBroadcastGroups> => {
    const params: Record<string, any> = { page, per_page: perPage };
    if (dateFrom) params.date_from = dateFrom;
    if (filters?.categoryId) params.category_id = filters.categoryId;
    if (filters?.metaUserName) params.meta_user_name = filters.metaUserName;
    if (filters?.projectId) params.project_id = filters.projectId;
    const response = await apiClient.get<PaginatedBroadcastGroups>('/meta/broadcasts/groups', { params });
    return response.data;
};

// Opções pros dropdowns dos filtros da tela de Broadcasts.
// Cacheada server-side por 60s.
export const getBroadcastFilterOptions = async (): Promise<BroadcastFilterOptions> => {
    const response = await apiClient.get<BroadcastFilterOptions>('/meta/broadcasts/filters');
    return response.data;
};

export const getBroadcastGroupDetails = async (groupId: number): Promise<BroadcastGroupDetail> => {
    const response = await apiClient.get<BroadcastGroupDetail>(`/meta/broadcasts/group/${groupId}/details`);
    return response.data;
};

export const getBroadcastGroupExecutions = async (
    groupId: number,
    page: number = 1,
    perPage: number = 20,
    search?: string
): Promise<PaginatedExecutions> => {
    const params: Record<string, any> = { page, per_page: perPage };
    if (search) params.search = search;
    const response = await apiClient.get<PaginatedExecutions>(`/meta/broadcasts/group/${groupId}/executions`, {
        params,
    });
    return response.data;
};

// Listar todos os broadcasts (admin)
export const getAllBroadcasts = async (): Promise<BroadcastResponse[]> => {
    const response = await apiClient.get<BroadcastResponse[]>('/meta/broadcasts/all');
    return response.data;
};

// Obter broadcast específico
export const getBroadcast = async (broadcastId: number): Promise<BroadcastResponse> => {
    const response = await apiClient.get<BroadcastResponse>(`/meta/broadcasts/${broadcastId}`);
    return response.data;
};

// Criar novo broadcast
export const createBroadcast = async (data: CreateBroadcastRequest): Promise<BroadcastResponse> => {
    const response = await apiClient.post<BroadcastResponse>('/meta/broadcasts', data);
    return response.data;
};

// Atualizar broadcast
export const updateBroadcast = async (
    broadcastId: number,
    data: UpdateBroadcastRequest
): Promise<BroadcastResponse> => {
    const response = await apiClient.put<BroadcastResponse>(`/meta/broadcasts/${broadcastId}`, data);
    return response.data;
};

// Atualizar TODOS os broadcasts de um grupo (mesmo template/agendamento).
// Bloqueado pelo backend se faltar < 5min pro envio (retorna HTTP 409).
export const updateBroadcastGroup = async (
    groupBroadcastId: number,
    data: UpdateBroadcastRequest,
): Promise<{ message: string; updated_count: number }> => {
    const response = await apiClient.put<{ message: string; updated_count: number }>(
        `/meta/broadcasts/group/${groupBroadcastId}`, data,
    );
    return response.data;
};

// Deletar broadcast (soft delete)
export const deleteBroadcast = async (broadcastId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/meta/broadcasts/${broadcastId}`);
    return response.data;
};

// Cancelar todos os broadcasts pendentes de um grupo
export const cancelBroadcastGroup = async (
    groupBroadcastId: number
): Promise<CancelBroadcastGroupResponse> => {
    const response = await apiClient.post<CancelBroadcastGroupResponse>(
        `/meta/broadcasts/group/${groupBroadcastId}/cancel`
    );
    return response.data;
};


// Validar variáveis das páginas para um template de broadcast
export const validateBroadcastVariables = async (
    data: ValidateVariablesRequest
): Promise<ValidateVariablesResponse> => {
    const response = await apiClient.post<ValidateVariablesResponse>('/meta/broadcasts/validate-variables', data);
    return response.data;
};

// Copiar variáveis de uma página para outras
export const copyPageVariables = async (
    data: VariablesCopyRequest
): Promise<VariablesCopyResponse> => {
    const response = await apiClient.post<VariablesCopyResponse>('/meta/pages/variables-copy', data);
    return response.data;
};

// ========== UTILITY TEMPLATES (broadcast) ==========

export interface BroadcastUtilityTemplateApprovedPage {
    page_id: string;
    esteira_id: number | null;
    esteira_name: string | null;
    approved_at: string | null;
}

export interface BroadcastUtilityTemplate {
    id: number;
    title: string | null;
    name: string | null;
    image_url: string | null;
    text_content: string | null;
    subtitle: string | null;
    buttons_json: unknown;
    approved_pages: BroadcastUtilityTemplateApprovedPage[];
    approved_url_examples: string[];
    coverage_approved: number;
    coverage_total: number;
}

export interface BroadcastUtilityTemplatesResponse {
    templates: BroadcastUtilityTemplate[];
    pages_requested: number;
}

export const getBroadcastUtilityTemplates = async (
    pageIds: number[],
): Promise<BroadcastUtilityTemplatesResponse> => {
    if (!pageIds.length) return { templates: [], pages_requested: 0 };
    const params = new URLSearchParams();
    pageIds.forEach((id) => params.append('page_ids', String(id)));
    const response = await apiClient.get<BroadcastUtilityTemplatesResponse>(
        `/meta/broadcasts/templates-for-pages?${params.toString()}`,
    );
    return response.data;
};

export interface BroadcastTemplateSubmitResult {
    page_id: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ERROR';
    meta_template_id?: string;
    reason?: string;
    detail?: string;
}

export interface BroadcastTemplateSubmitResponse {
    source: string;
    source_id: number;
    esteira_group_id: number | null;
    submitted_pages: number;
    results: BroadcastTemplateSubmitResult[];
}

export const submitBroadcastTemplateFree = async (
    templateId: number,
    pageIds: string[],
    overrideUrl?: string,
): Promise<BroadcastTemplateSubmitResponse> => {
    const response = await apiClient.post<BroadcastTemplateSubmitResponse>(
        `/meta/broadcasts/templates/${templateId}/submit`,
        { page_ids: pageIds, override_url: overrideUrl || null },
    );
    return response.data;
};

// ========== PAGES API ==========


// Obter imagem da página diretamente do Meta Graph API
export const getPagePicture = async (pageId: string, accessToken: string): Promise<string> => {
    try {
        const response = await fetch(
            `https://graph.facebook.com/v18.0/${pageId}/picture?type=large&redirect=false&access_token=${accessToken}`
        );

        if (!response.ok) {
            throw new Error('Erro ao carregar imagem da página');
        }

        const data = await response.json();
        return data.data.url;
    } catch (error) {
        console.error('Erro ao buscar imagem da página:', error);
        throw error;
    }
};
